import { FormConfig } from 'components/common/form/FormConfig';
import React, { useCallback, useMemo } from 'react';
import i18n from 'i18n';
import { Form } from 'components/common/form/Form';
import { Formik, useFormikContext } from 'formik';
import { LoadingIndicator } from 'components/common/LoadingIndicator';
import {
  BeaconMessageFormType,
  BeaconConnectionOption
} from 'core/beaconMessage/BeaconMessage';
import moment from 'moment';
import { Trans } from 'react-i18next';
import { Dayparts, getInitDaypart } from 'components/Dayparts/Dayparts';
import { useCreativeSetupSection } from './hooks/useCreativeSetupSection';
import { validateEmpty } from 'utils/ValidateUtils';
// import { useDeviceSetupSection } from './hooks/useDeviceSetupSection';
// import { DefaultEditLimitationModel } from 'containers/Limitations/EditLimitationModel';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';

export type BeaconMessageFormProps = {
  type: 'create' | 'edit' | 'summary';
  formData: BeaconMessageFormType;
  defaultBeaconMessage: BeaconMessageFormType;
  // limitationModel: DefaultEditLimitationModel | undefined;
  beaconConnectionOptions: BeaconConnectionOption[];
  availableChannels?: SelectOptions[];
  onSubmit: (beaconMessage: BeaconMessageFormType) => void;
  // onChannelGroupSettingChange: (
  //   channelGroupSetting: ChannelGroupSetting
  // ) => void;
  onNavigationCancel?: () => void;
};

const BeaconMessageFormInner: React.FC<{
  type: 'create' | 'edit' | 'summary';
  // limitationModel: DefaultEditLimitationModel | undefined;
  defaultBeaconMessage: BeaconMessageFormType;
  beaconConnectionOptions: BeaconConnectionOption[];
  availableChannels?: SelectOptions[];
  // onChannelGroupSettingChange: (
  //   channelGroupSetting: ChannelGroupSetting
  // ) => void;
  onNavigationCancel?: () => void;
}> = ({
  type,
  // limitationModel,
  defaultBeaconMessage,
  beaconConnectionOptions,
  availableChannels,
  // onChannelGroupSettingChange,
  onNavigationCancel
}) => {
  const formikProps = useFormikContext<BeaconMessageFormType>();
  const { values, setValues, setFieldValue, setFieldTouched } = formikProps;

  const { loading, section: creativeSection } = useCreativeSetupSection(
    beaconConnectionOptions
  );
  // const deviceSection = useDeviceSetupSection(limitationModel);

  const minDate = useMemo(
    () => moment().startOf('day').format('YYYY-MM-DD HH:mm:ss'),
    []
  );

  const maxDate = useMemo(
    () =>
      moment()
        .startOf('day')
        .add(365, 'days')
        .endOf('day')
        .format('YYYY-MM-DD HH:mm:ss'),
    []
  );

  const renderDaypart = useCallback(
    (props: any) => <Dayparts {...props} />,
    []
  );

  const daypartEnabled = values.dayPart?.enabled;

  const onDaypartSwitchChanged = useCallback(
    (checked: boolean) => {
      if (!checked) {
        setFieldValue('dayPart', { ...getInitDaypart(), enabled: false });
      }
    },
    [setFieldValue]
  );

  const setRelatedFieldTouched = useCallback(() => {
    setFieldTouched('dayPart', true);
    setFieldTouched('dayPart.enabled', true);
  }, [setFieldTouched]);

  const start = moment(values.startDate);
  const end = moment(values.endDate);
  const days = end.diff(start, 'days') + 1;
  const isBeaconMessageStart =
    type === 'edit' &&
    moment(defaultBeaconMessage.startDate).isBefore(moment());

  const validateDateRange = useCallback(
    (startDate: string, endDate: string) => {
      let start = new Date(startDate);
      let end = new Date(endDate);
      const diffTime = Math.abs(end.getTime() - start.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays > 92) {
        return i18n.t('campaign.descriptions.moreThanDays', {
          days: 92
        });
      }
      const now = moment();
      const shouldCheckStartDay =
        type === 'create' ||
        moment(defaultBeaconMessage.startDate).isAfter(now);
      if (
        shouldCheckStartDay &&
        moment(startDate).add(1, 'hours').isBefore(now)
      ) {
        return i18n.t('campaign.descriptions.createBeforeNow');
      }
      const shouldCheckEndDay =
        type === 'edit' && defaultBeaconMessage.endDate !== endDate;
      if (shouldCheckEndDay && moment(endDate).isBefore(now)) {
        return i18n.t('campaign.descriptions.endDayBeforeNow');
      }
    },
    [defaultBeaconMessage, type]
  );

  const formConfig = useMemo(() => {
    const fieldsBuilder = new FormConfig.FieldsBuilder();
    const onChannelGroupChange = (channelId: number) => {
      const channelGroupSetting = ChannelGroupSetting.fromId(channelId);
      // onChannelGroupSettingChange(channelGroupSetting);
      const adTypeSetting = channelGroupSetting.getActiveCampaignAdTypes()[0];
      setValues((beaconMessage) => ({
        ...beaconMessage,
        channel: channelGroupSetting.key,
        channelId,
        adTypeId: adTypeSetting.adTypeId || 0,
        limitations: {
          include: [],
          exclude: [],
          preferred: [],
          nonPreferred: [],
          other: []
        },
        creative: {
          ...beaconMessage.creative,
          creativeType: adTypeSetting.bannerTypes[0].bannerTypeId
        }
      }));
    };

    if (availableChannels && availableChannels.length > 1) {
      fieldsBuilder.addFormikSelect({
        label: i18n.t('beaconMessageForm.labels.channel'),
        name: 'channelId',
        simpleValue: true,
        options: availableChannels,
        onChange: onChannelGroupChange,
        validate: validateEmpty
      });
    }

    // === 基本設定 ===
    fieldsBuilder
      .addFormikInput({
        label: i18n.t('beaconMessageForm.labels.name'),
        name: 'name',
        validate: validateEmpty
      })
      .addFormikDateRangePicker({
        label: i18n.t('beaconMessageForm.labels.schedule'),
        name: 'schedule',
        minDate,
        maxDate,
        showTimePicker: true,
        startDateFormikName: 'startDate',
        endDateFormikName: 'endDate',
        timeInterval: 60,
        startDatePickerDisabled: isBeaconMessageStart,
        hoverHint: i18n.t('campaignInfo.labels.dateCannotModifyHint'),
        hint: (
          <Trans
            i18nKey='campaign.descriptions.campaignSchedulingDay'
            values={{ days }}
            components={{ 1: <span className='text-dark' /> }}
          />
        ),
        validate: validateDateRange
      })
      .addFormikSwitch({
        label: i18n.t('beaconMessageForm.labels.dayparts'),
        name: 'dayPart.enabled',
        onChange: onDaypartSwitchChanged
      });

    if (daypartEnabled) {
      fieldsBuilder.addFormikCustom({
        label: '',
        name: 'dayPart',
        onChange: setRelatedFieldTouched,
        render: renderDaypart
      });
    }

    const basicSection = new FormConfig.SectionBuilder(fieldsBuilder.build())
      .withTitle(i18n.t('beaconMessageSetupFlowPage.titles.basicSection'))
      .build();

    return (
      new FormConfig.Builder()
        .addSection(basicSection)
        .addSection(creativeSection)
        // .addSection(deviceSection)
        .build()
    );
  }, [
    daypartEnabled,
    isBeaconMessageStart,
    creativeSection,
    // deviceSection,
    days,
    minDate,
    maxDate,
    availableChannels,
    setValues,
    renderDaypart,
    onDaypartSwitchChanged,
    setRelatedFieldTouched,
    validateDateRange
    // onChannelGroupSettingChange
  ]);

  const renderFormBtns = () => (
    <>
      <Form.SubmitButton>
        {i18n.t('common.buttons.completeAndCheck')}
      </Form.SubmitButton>
      <Form.CancelButton />
    </>
  );

  return (
    <>
      {loading && <LoadingIndicator />}
      <Form
        formikProps={formikProps}
        formConfig={formConfig}
        renderFormBtns={renderFormBtns}
        onNavigationCancel={onNavigationCancel}
      />
    </>
  );
};

export const BeaconMessageForm: React.FC<BeaconMessageFormProps> = ({
  type,
  // limitationModel,
  formData,
  defaultBeaconMessage,
  beaconConnectionOptions,
  availableChannels,
  onSubmit,
  // onChannelGroupSettingChange,
  onNavigationCancel
}) => {
  if (!formData) {
    return <LoadingIndicator />;
  }

  return (
    <Formik
      initialValues={formData}
      onSubmit={onSubmit}
      validateOnBlur={false}
      enableReinitialize
    >
      <BeaconMessageFormInner
        type={type}
        // limitationModel={limitationModel}
        defaultBeaconMessage={defaultBeaconMessage}
        beaconConnectionOptions={beaconConnectionOptions}
        availableChannels={availableChannels}
        // onChannelGroupSettingChange={onChannelGroupSettingChange}
        onNavigationCancel={onNavigationCancel}
      />
    </Formik>
  );
};
